<script setup>
import { computed, onBeforeUnmount, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useAccount } from 'dashboard/composables/useAccount';
import { useLiveRefresh } from 'dashboard/composables/useLiveRefresh';
import addDays from 'date-fns/addDays';
import endOfDay from 'date-fns/endOfDay';
import format from 'date-fns/format';
import fromUnixTime from 'date-fns/fromUnixTime';
import getUnixTime from 'date-fns/getUnixTime';
import startOfDay from 'date-fns/startOfDay';
import subDays from 'date-fns/subDays';
import BarChart from 'shared/components/charts/BarChart.vue';
import {
  buildDatasetHoverDimmingHandler,
  buildIntercomLikeBarTooltip,
  removeTooltipEl,
} from 'shared/components/charts/barInteractions';
import { STATUS } from 'dashboard/store/constants';

const METRIC_KEY = 'conversations_count';
const LAST_7_DAYS_OFFSET = 6;

const store = useStore();
const { t } = useI18n();
const { accountScopedRoute } = useAccount();

const accountSummary = useMapGetter('getAccountSummary');
const accountReports = useMapGetter('getAccountReports');
const accountSummaryFetchingStatus = useMapGetter(
  'getAccountSummaryFetchingStatus'
);

const dateRange = computed(() => {
  const toDate = endOfDay(new Date());
  const fromDate = startOfDay(subDays(toDate, LAST_7_DAYS_OFFSET));
  return {
    from: getUnixTime(fromDate),
    to: getUnixTime(toDate),
    fromDate,
  };
});

const isLoading = computed(() => {
  return (
    accountSummaryFetchingStatus.value === STATUS.FETCHING ||
    accountReports.value?.isFetching?.[METRIC_KEY]
  );
});

const hasSummaryError = computed(
  () => accountSummaryFetchingStatus.value === STATUS.FAILED
);

const conversationTimeline = computed(() => {
  return accountReports.value?.data?.[METRIC_KEY] || [];
});

const dayRows = computed(() => {
  return Array.from({ length: 7 }, (_, index) => {
    const date = addDays(dateRange.value.fromDate, index);
    const ts = getUnixTime(date);
    return {
      timestamp: ts,
      dayKey: format(date, 'yyyy-MM-dd'),
      shortLabel: format(date, 'dd MMM'),
      tooltipLabel: format(date, "dd MMM''yy"),
    };
  });
});

const timelineValueMap = computed(() => {
  return (conversationTimeline.value || []).reduce((acc, item) => {
    const dayKey = format(fromUnixTime(item.timestamp), 'yyyy-MM-dd');
    acc[dayKey] = Number(item.value || 0);
    return acc;
  }, {});
});

const totalChats = computed(() =>
  Number(accountSummary.value?.conversations_count || 0)
);
const previousTotalChats = computed(() =>
  Number(accountSummary.value?.previous?.conversations_count || 0)
);
const totalChatsDiff = computed(
  () => totalChats.value - previousTotalChats.value
);
const diffIsNegative = computed(() => totalChatsDiff.value < 0);
const absDiff = computed(() => Math.abs(totalChatsDiff.value));

const queuedVisitors = computed(() =>
  Number(accountSummary.value?.queued_visitors_count ?? 0)
);

const missedChats = computed(() =>
  Number(accountSummary.value?.missed_chats_count || 0)
);
const previousMissedChats = computed(() =>
  Number(accountSummary.value?.previous?.missed_chats_count || 0)
);
const missedChatsDiff = computed(
  () => missedChats.value - previousMissedChats.value
);
const missedDiffIsNegative = computed(() => missedChatsDiff.value < 0);
const missedAbsDiff = computed(() => Math.abs(missedChatsDiff.value));

const startedByCustomers = computed(() =>
  Number(accountSummary.value?.conversations_started_by_customer_count || 0)
);
const enteredChats = computed(() =>
  Math.max(startedByCustomers.value - missedChats.value, 0)
);
const enteredChatRate = computed(() => {
  if (!startedByCustomers.value) return null;
  return ((enteredChats.value / startedByCustomers.value) * 100).toFixed(1);
});

const csatScore = computed(() => {
  const v = accountSummary.value?.csat_survey_score;
  return v != null ? Math.round(v) : null;
});

const tooltipId = 'overview-last7days-tooltip';

const chartCollection = computed(() => ({
  labels: dayRows.value.map(row => row.shortLabel),
  datasets: [
    {
      label: t('TOTAL_CHATS_REPORT.TOTAL_CHATS'),
      data: dayRows.value.map(row =>
        Number(timelineValueMap.value[row.dayKey] || 0)
      ),
      backgroundColor: '#1D75E5',
      borderRadius: 4,
      categoryPercentage: 0.72,
      barPercentage: 0.95,
    },
  ],
}));

const chartOptions = computed(() => ({
  responsive: true,
  maintainAspectRatio: false,
  animation: { duration: 0 },
  interaction: { mode: 'nearest', intersect: true },
  onHover: buildDatasetHoverDimmingHandler(),
  plugins: {
    legend: { display: false },
    tooltip: {
      enabled: false,
      displayColors: false,
      external: buildIntercomLikeBarTooltip({
        tooltipId,
        titleForIndex: index => dayRows.value[index]?.tooltipLabel || '',
        seriesLabelForDataset: () => t('TOTAL_CHATS_REPORT.TOTAL_CHATS'),
        showPercent: false,
      }),
    },
  },
  scales: {
    y: {
      beginAtZero: true,
      ticks: { callback: value => Number(value).toLocaleString() },
    },
  },
}));

const fetchData = async () => {
  const payload = {
    from: dateRange.value.from,
    to: dateRange.value.to,
    type: 'account',
    groupBy: 'day',
    businessHours: false,
  };
  store.dispatch('fetchAccountSummary', payload);
  store.dispatch('fetchAccountReport', { metric: METRIC_KEY, ...payload });
};

const { startRefetching } = useLiveRefresh(fetchData);

onMounted(() => {
  fetchData();
  startRefetching();
});

onBeforeUnmount(() => {
  removeTooltipEl(tooltipId);
});
</script>

<template>
  <section class="ov7-card">
    <div class="ov7-header">
      <div class="ov7-title">
        {{ t('REPORT.DATE_RANGE_OPTIONS.LAST_7_DAYS') }}
      </div>
      <router-link
        :to="accountScopedRoute('account_last_7_days_reports')"
        class="ov7-link"
      >
        View details
      </router-link>
    </div>

    <div class="ov7-body">
      <div class="ov7-kpis">
        <div class="ov7-kpi">
          <div class="ov7-kpi-label">Total chats</div>
          <div class="ov7-kpi-value">{{ totalChats.toLocaleString() }}</div>
          <div
            v-if="previousTotalChats || totalChatsDiff !== 0"
            class="ov7-kpi-sub"
            :class="diffIsNegative ? 'is-neg' : 'is-pos'"
          >
            <span>{{ diffIsNegative ? '-' : '+'
              }}{{ absDiff.toLocaleString() }}</span>
            <span class="ov7-kpi-sub-muted">from previous 7 days</span>
          </div>
        </div>

        <div class="ov7-kpi">
          <div class="ov7-kpi-label">Queued visitors</div>
          <div class="ov7-kpi-value">{{ queuedVisitors.toLocaleString() }}</div>
          <div class="ov7-kpi-sub ov7-kpi-sub-muted">
            Open &amp; unassigned (waiting for agent)
          </div>
        </div>

        <div class="ov7-kpi">
          <div class="ov7-kpi-label">Missed chats</div>
          <div class="ov7-kpi-value">{{ missedChats.toLocaleString() }}</div>
          <div
            v-if="previousMissedChats || missedChatsDiff !== 0"
            class="ov7-kpi-sub"
            :class="missedDiffIsNegative ? 'is-pos' : 'is-neg'"
          >
            <span>{{ missedDiffIsNegative ? '-' : '+'
              }}{{ missedAbsDiff.toLocaleString() }}</span>
            <span class="ov7-kpi-sub-muted">vs previous 7 days</span>
          </div>
        </div>

        <div class="ov7-kpi">
          <div class="ov7-kpi-label">Entered chat rate</div>
          <div class="ov7-kpi-value">
            {{ enteredChatRate != null ? `${enteredChatRate}%` : '—' }}
          </div>
          <div class="ov7-kpi-sub ov7-kpi-sub-muted">
            Started by customer minus missed chats
          </div>
        </div>

        <div class="ov7-kpi">
          <div class="ov7-kpi-label">CSAT</div>
          <div class="ov7-kpi-value">
            {{ csatScore != null ? `${csatScore}%` : '—' }}
          </div>
          <div class="ov7-kpi-sub ov7-kpi-sub-muted">
            Customer satisfaction score
          </div>
        </div>
      </div>

      <div class="ov7-chart">
        <woot-loading-state
          v-if="isLoading"
          :message="$t('REPORT.LOADING_CHART')"
          class="h-full"
        />
        <div
          v-else-if="hasSummaryError"
          class="ov7-chart-error text-sm text-n-ruby-10 flex items-center justify-center h-full"
        >
          {{ t('REPORT.SUMMARY_FETCHING_FAILED') }}
        </div>
        <BarChart
          v-else
          :collection="chartCollection"
          :chart-options="chartOptions"
        />
      </div>
    </div>
  </section>
</template>

<style scoped>
.ov7-card {
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  background: #ffffff;
  overflow: hidden;
}

.ov7-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 16px 18px;
  border-bottom: 1px solid #e5e7eb;
}

.ov7-title {
  font-size: 14px;
  font-weight: 700;
  color: #111827;
}

.ov7-link {
  font-size: 13px;
  font-weight: 600;
  color: #111827;
  text-decoration: underline;
}

.ov7-body {
  padding: 16px 18px 18px;
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.ov7-kpis {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 12px;
}

@media (max-width: 1100px) {
  .ov7-kpis {
    grid-template-columns: 1fr;
  }
}

.ov7-kpi {
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  background: #f8fafc;
  padding: 12px;
}

.ov7-kpi-label {
  font-size: 12px;
  font-weight: 700;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.06em;
}

.ov7-kpi-value {
  margin-top: 6px;
  font-size: 24px;
  line-height: 28px;
  font-weight: 800;
  color: #111827;
}

.ov7-kpi-sub {
  margin-top: 6px;
  font-size: 12px;
  font-weight: 700;
  color: #111827;
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
}

.ov7-kpi-sub-muted {
  font-weight: 600;
  color: #6b7280;
}

.ov7-kpi-sub.is-pos {
  color: #059669;
}
.ov7-kpi-sub.is-neg {
  color: #dc2626;
}

.ov7-chart {
  height: 260px;
}
</style>
